import * as React from "react";
import { FileText, ChevronDown, ChevronUp } from "lucide-react";
import StatusBadge from "../atoms/StatusBadge";
import { SchemaExtractionData } from "../../../../types/dataTypes";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface DocumentCardProps {
  schemaData: SchemaExtractionData;
  className?: string;
  children?: React.ReactNode;
  defaultExpanded?: boolean;
}

const DocumentCard: React.FC<DocumentCardProps> = ({ 
  schemaData, 
  className = '',
  children,
  defaultExpanded = true
}) => {
  const [isOpen, setIsOpen] = React.useState(defaultExpanded);

  const getStatusForBadge = (status: SchemaExtractionData['status']): 'completed' | 'processing' | 'error' => {
    if (status === 'completed') return 'completed';
    if (status === 'processing' || status === 'pending') return 'processing';
    return 'error';
  };

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={cn("w-full mb-4", className)}
    >
      <Card className="overflow-hidden">
        <CollapsibleTrigger asChild>
          <div 
            className="flex flex-col gap-2 cursor-pointer md:flex-row md:items-center md:justify-between p-4 hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-md flex items-center justify-center text-primary shrink-0">
                <FileText />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-[15px] font-semibold text-slate-800 m-0 mb-1 break-words md:text-base">
                  {schemaData.name}
                </h3>
                <p className="text-[11px] text-slate-500 m-0 leading-relaxed md:text-xs">
                  Created: {schemaData.uploadDate.toLocaleDateString('en-US')} • 
                  Fields: {schemaData.extractedFields.length}
                  {schemaData.description && ` • ${schemaData.description}`}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 mt-2 md:mt-0 ml-12 md:ml-0">
              <StatusBadge 
                status={getStatusForBadge(schemaData.status)} 
              />
              <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 shrink-0">
                {isOpen ? <ChevronUp /> : <ChevronDown />}
              </Button>
            </div>
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <div className="px-4 pb-4 border-t border-slate-100 pt-4">
            {children}
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default DocumentCard;
